import { useMemo, useCallback } from 'react';
import { FormSchemaConfig } from '../../types/form/schema.types';
import { FormState } from '../../types/core/form-state.types';
import {
  FIELD_TYPES,
  Column,
  formatCurrencyValue,
} from '@tw/tw-component-library';
import { WarningText } from '../../../../common/CustomTextDisplay';
import { FileDisplay } from '../../components/FileDisplay';
import {
  validatePaymentRowForAdd,
  validatePaymentRowForUpdate,
  createValidationContext,
} from '../../../../../utils/paymentValidation';
import {
  validateAccountNumber,
  validateBicSwift,
  validateIban,
  validateSortCode,
} from '../Utils/bankingValidation';
import { UseAuth } from '../../../../../contexts/AuthContext';
import { isUserResponsibleLawyer } from '../../../../../utils/permissions';

/**
 * Payment Details Schema - Centralized Disabled State Management
 *
 * This schema uses a centralized disabled state management system to ensure
 * consistent behavior across all form fields. The `disabledState` object provides:
 *
 * - Single source of truth for all disabled states
 * - Helper methods for different field types
 * - Automatic updates when form state changes
 * - DRY (Don't Repeat Yourself) principle compliance
 *
 * Usage:
 * - Use `disabledState.isFieldDisabled('readonly')` for read-only fields
 * - Use `disabledState.isFieldDisabled('editable')` for standard editable fields
 * - Use `disabledState.isFieldDisabled('conditional')` for fields that should only be disabled when form is completed
 * - Use specific helpers like `disabledState.isTagPickerDisabled()` for field-specific logic
 */

export interface PaymentRow {
  location?: string;
  payee: string;
  currency: string;
  currencyCustom?: string;
  amount: string;
  accno: string;
  sortCode?: string;
  iban?: string;
  swiftBicCode?: string;
  bankName: string;
  bankAddress?: string;
  type?: string;
  purpose: string;
  purposeCustom?: string;
  deductBankCharges?: string;
  reference?: string;
  ledgerDesc?: string;
  formattedAmount?: string; // For display purposes with currency symbol
}

/**
 * Function to calculate payment total from gridData
 *
 * @param formData The entire form data object
 * @returns The sum of all amount values in gridData
 */

const calculatePaymentTotal = (
  _values: unknown[],
  formData: Record<string, unknown>,
): number => {
  const gridData = formData.gridData || [];

  // Handle case where gridData might have a data property (after adding rows)
  const rowsToProcess = Array.isArray(
    (gridData as Record<string, unknown>).data,
  )
    ? (gridData as Record<string, unknown>).data
    : Array.isArray(gridData)
      ? gridData
      : [];

  return (rowsToProcess as PaymentRow[]).reduce(
    (total: number, row: PaymentRow) => {
      const amount =
        typeof row.amount === 'number'
          ? row.amount
          : parseFloat(row.amount) || 0;
      return total + amount;
    },
    0,
  );
};

/**
 * Centralized disabled state management
 * This provides a single source of truth for all disabled states in the form
 */
interface DisabledStateManager {
  // Form completion state
  isFormCompleted: boolean;
  isFormCancelled: boolean;

  // Payment count states
  isSinglePaymentMode: boolean;
  isMultiplePaymentMode: boolean;

  // Helper functions for common disabled patterns
  isFieldDisabled: (
    fieldType?: 'readonly' | 'editable' | 'conditional',
  ) => boolean;
  isTableDisabled: () => boolean;
  isPaymentTotalDisabled: () => boolean;

  // Additional helper methods for specific field types
  isTagPickerDisabled: () => boolean;
  isFileUploadDisabled: () => boolean;
  isTextAreaDisabled: () => boolean;
  isRadioDisabled: () => boolean;
  isSelectDisabled: () => boolean;
}

/**
 * usePaymentDetailsSchema
 *
 * A React hook that returns a dynamic form schema object (FormSchemaConfig) for handling payment details.
 * It sets up columns for a table, defines sections and fields in a form, and conditionally shows/hides
 * certain fields using the "modern" dependency format (conditions array + logicalOperator).
 *
 * The returned value from this hook is a FormSchemaConfig object that can be used by a form builder or
 * renderer to display and manage the described fields, sections, and dependencies.
 *
 * @param formState - The current form state including user data and completed sections
 * @returns A FormSchemaConfig object describing the entire "Payment Request Form" structure
 */
export const usePaymentDetailsSchema = (
  formState: FormState,
): FormSchemaConfig => {
  // Get current user and spoofed user for permission checks
  const { currentUser, spoofedUser } = UseAuth();

  // ============= Centralized Disabled State Management =============
  const isEditAllowed =
    (Array.isArray(formState.data?.userAssignedSections)
      ? (formState.data?.userAssignedSections as string[]).includes(
          'payment_details',
        )
      : false) ||
    isUserResponsibleLawyer(
      formState.data?.responsible_lawyer,
      currentUser,
      spoofedUser?.value || null,
    );
  const disabledState = useMemo<DisabledStateManager>(() => {
    const isFormCompleted =
      formState.completedSections?.includes('payment_details') || false;
    // For rejected forms, we want them to be editable like they haven't been completed
    const isFormRejected = formState.isRejected || false;
    const isFormCancelled = formState.isCancelled || false;
    const isSinglePaymentMode =
      formState.data?.payments_count === '1-5Payments';
    const isMultiplePaymentMode =
      formState.data?.payments_count === '5+Payments';

    return {
      isFormCompleted,
      isFormCancelled,
      isSinglePaymentMode,
      isMultiplePaymentMode,

      // Helper function for field disabled states
      isFieldDisabled: (
        fieldType: 'readonly' | 'editable' | 'conditional' = 'editable',
      ) => {
        // If form is cancelled, everything is disabled
        if (isFormCancelled) {
          return true;
        }

        switch (fieldType) {
          case 'readonly':
            return true; // Always disabled for readonly fields
          case 'conditional':
            return (isFormCompleted && !isFormRejected) || !isEditAllowed;
          case 'editable':
          default:
            return (isFormCompleted && !isFormRejected) || !isEditAllowed;
        }
      },

      // Helper function for table disabled state
      isTableDisabled: () =>
        isFormCancelled ||
        (isFormCompleted && !isFormRejected) ||
        !isEditAllowed,

      // Helper function for payment total disabled state
      isPaymentTotalDisabled: () =>
        isFormCancelled ||
        isSinglePaymentMode ||
        (isFormCompleted && !isFormRejected) ||
        !isEditAllowed,

      // Additional helper methods for specific field types
      isTagPickerDisabled: () =>
        isFormCancelled ||
        (isFormCompleted && !isFormRejected) ||
        !isEditAllowed,
      isFileUploadDisabled: () =>
        isFormCancelled ||
        (isFormCompleted && !isFormRejected) ||
        !isEditAllowed,
      isTextAreaDisabled: () =>
        isFormCancelled ||
        (isFormCompleted && !isFormRejected) ||
        !isEditAllowed,
      isRadioDisabled: () =>
        isFormCancelled ||
        (isFormCompleted && !isFormRejected) ||
        !isEditAllowed,
      isSelectDisabled: () =>
        isFormCancelled ||
        (isFormCompleted && !isFormRejected) ||
        !isEditAllowed,
    };
  }, [
    formState.completedSections,
    formState.data?.payments_count,
    isEditAllowed,
    formState.isRejected,
    formState.isCancelled,
  ]);

  type TableFormField = {
    name: string;
    label: string;
    type: string;
    required?: boolean;
    placeholder?: string;
    options?: Array<{ value: string; label: string }>;
    width?: string;
    hidden?: boolean;
    validate?: (value: unknown) => string | undefined;
    dependencies?: Array<{
      type: 'show' | 'hide';
      logicalOperator: 'AND' | 'OR';
      conditions: Array<{
        field: string;
        operator:
          | 'equals'
          | 'notEquals'
          | 'contains'
          | 'notContains'
          | 'greaterThan'
          | 'lessThan';
        value: string | number | boolean;
      }>;
    }>;
    extras?: { textArea?: { rows?: number; placeholder?: string } };
  };

  type TableFormSection = {
    title: { text: string; type: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' };
    fields: TableFormField[];
    sections?: TableFormSection[];
    isGroupedAccordion?: boolean;
  };

  // Helper function to create validated table form fields
  const createValidatedTableField = useCallback(
    (
      column: Column<PaymentRow>,
      type: string,
      isTextArea: boolean = false,
    ): TableFormField => {
      const field: TableFormField = {
        type,
        name: String(column.key),
        label: column.header,
        required: !!column.required,
        placeholder:
          column.fieldConfig?.placeholder ||
          `Enter ${String(column.header).toLowerCase()}`,
        validate: (value: unknown) => {
          // Basic required field validation
          if (column.required && (!value || String(value).trim() === '')) {
            return `${column.header} is required`;
          }

          // Field-specific validation
          switch (column.key) {
            case 'amount':
              if (value && (isNaN(Number(value)) || Number(value) <= 0)) {
                return 'Amount must be a positive number';
              }
              break;
            case 'currencyCustom':
              if (value && String(value).trim().length < 2) {
                return 'Currency code/name must be at least 2 characters';
              }
              break;
            case 'purposeCustom':
              if (value && String(value).trim().length < 2) {
                return 'Purpose description must be at least 2 characters';
              }
              break;
            case 'accno':
              if (!value || String(value).trim() === '') return undefined;
              return validateAccountNumber(value, String(column.header));
            case 'iban':
              if (!value || String(value).trim() === '') return undefined;
              return validateIban(value, String(column.header));
            case 'swiftBicCode':
              if (!value || String(value).trim() === '') return undefined;
              return validateBicSwift(value, String(column.header));
            case 'sortCode':
              if (!value || String(value).trim() === '') return undefined;
              return validateSortCode(value, String(column.header));
            case 'bankName':
              if (value && String(value).trim().length < 2) {
                return 'Bank name must be at least 2 characters';
              }
              break;
          }
          return undefined;
        },
      };

      if (type === 'select' && column.options) {
        field.options = column.options.map((opt) => ({
          value: String(opt.value),
          label: opt.label,
        }));
      }

      if (isTextArea && column.extras?.textArea) {
        field.extras = {
          textArea: {
            rows: column.extras.textArea.rows,
            placeholder: column.extras.textArea.placeholder,
          },
        };
      }

      return field;
    },
    [],
  );

  // ============= Dynamic Column Definitions with Dependencies =============
  const columns = useMemo<Column<PaymentRow>[]>(() => {
    const allColumns: Column<PaymentRow>[] = [
      // 1. Location
      // {
      //   key: 'location',
      //   header: 'Location',
      //   sortable: true,
      //   required: true,
      //   type: FIELD_TYPES.Select,
      //   group: 'Payment Details',
      //   groupOrder: 1,
      //   options: [
      //     { label: 'Domestic UK', value: 'uk', disabled: false },
      //     { label: 'International', value: 'nonUk', disabled: false },
      //   ],
      //   fieldConfig: {
      //     placeholder: 'Select location',
      //   },
      // },
      // 2. Payee
      {
        key: 'payee',
        header: 'Payee',
        sortable: true,
        type: FIELD_TYPES.Text,
        required: true,
        group: 'Payment Details',
        groupOrder: 2,
        fieldConfig: {
          placeholder: 'Enter payee name',
        },
      },
      // 3. Currency
      {
        key: 'currency',
        header: 'Currency',
        sortable: true,
        type: FIELD_TYPES.Select,
        required: true,
        group: 'Payment Details',
        groupOrder: 3,
        options: [
          { label: 'GBP', value: 'GBP', disabled: false },
          { label: 'EUR', value: 'EUR', disabled: false },
          { label: 'USD', value: 'USD', disabled: false },
          { label: 'AED', value: 'AED', disabled: false },
          { label: 'Other', value: 'other', disabled: false },
        ],
        fieldConfig: {
          placeholder: 'Select currency',
        },
      },
      {
        key: 'currencyCustom',
        header: 'Currency (other)',
        sortable: true,
        type: FIELD_TYPES.Text,
        group: 'Payment Details',
        groupOrder: 4,
        fieldConfig: {
          placeholder: 'Enter currency code/name',
        },
      },
      // 4. Amount
      {
        key: 'amount',
        header: 'Amount',
        sortable: true,
        type: FIELD_TYPES.Text,
        required: true,
        group: 'Payment Details',
        groupOrder: 4,
        fieldConfig: {
          placeholder: 'Enter amount',
        },
        extras: {
          textInput: {
            formatCurrency: true,
          },
        },
      },
      // 5. Account Number (UK-specific)
      {
        key: 'accno',
        header: 'Account Number',
        sortable: true,
        type: 'accountNumber' as typeof FIELD_TYPES.Text,
        required: true,
        group: 'Banking Details',
        groupOrder: 1,
        fieldConfig: {
          placeholder: '12345678',
        },
      },
      // 6. Sort Code (UK-specific)
      {
        key: 'sortCode',
        header: 'Sort Code',
        sortable: true,
        required: true,
        type: 'sortCode' as typeof FIELD_TYPES.Text,
        group: 'Banking Details',
        groupOrder: 2,
        fieldConfig: {
          placeholder: '12-34-56',
        },
      },
      // 7. IBAN (International-specific)
      {
        key: 'iban',
        header: 'IBAN',
        sortable: true,
        required: true,
        type: 'iban' as typeof FIELD_TYPES.Text,
        group: 'Banking Details',
        groupOrder: 3,
        fieldConfig: {
          placeholder: 'GB82 WEST 1234 5698 7654 32',
        },
      },
      // 8. SWIFT/BIC Code (International-specific)
      {
        key: 'swiftBicCode',
        header: 'SWIFT/BIC Code',
        sortable: true,
        required: true,
        type: 'bicSwift' as typeof FIELD_TYPES.Text,
        group: 'Banking Details',
        groupOrder: 4,
        fieldConfig: {
          placeholder: 'ABCDGB2L',
        },
      },
      // 9. Bank Name (International-specific)
      {
        key: 'bankName',
        header: 'Bank Name',
        sortable: true,
        type: FIELD_TYPES.Text,
        required: true,
        group: 'Banking Details',
        groupOrder: 5,
        fieldConfig: {
          placeholder: 'Enter bank name',
        },
      },
      // 10. Bank Address (International-specific) - Add this field
      {
        key: 'bankAddress',
        header: 'Bank Address',
        sortable: true,
        type: FIELD_TYPES.Text,
        group: 'Banking Details',
        groupOrder: 6,
        extras: {
          textArea: {
            rows: 2,
            placeholder: 'Enter bank address',
          },
        },
        fieldConfig: {
          placeholder: 'Enter bank address',
        },
      },
      // 11. Account Type
      {
        key: 'type',
        header: 'Account Type',
        sortable: true,
        type: FIELD_TYPES.Select,
        required: true,
        group: 'Banking Details',
        groupOrder: 7,
        options: [
          { label: 'Personal', value: 'personal', disabled: false },
          { label: 'Business', value: 'business', disabled: false },
        ],
        fieldConfig: {
          placeholder: 'Select account type',
        },
      },
      // 12. Purpose
      {
        key: 'purpose',
        header: 'Purpose',
        sortable: true,
        type: FIELD_TYPES.Select,
        required: true,
        group: 'Transaction Details',
        groupOrder: 1,
        options: [
          {
            label: 'HLRP - Property Loan Repayment',
            value: 'hlrp',
            disabled: false,
          },
          {
            label: 'HLST - Property Loan Settlement',
            value: 'hlst',
            disabled: false,
          },
          {
            label: 'PLDS - Property Loan Disbursement',
            value: 'plds',
            disabled: false,
          },
          {
            label: 'PDEP - Property Deposit',
            value: 'pdep',
            disabled: false,
          },
          {
            label: 'PCOM - Property Completion Payment',
            value: 'pcom',
            disabled: false,
          },
          {
            label: 'PLRF - Property Loan Refinancing',
            value: 'plrf',
            disabled: false,
          },
          {
            label: 'Other',
            value: 'other',
            disabled: false,
          },
          { label: 'N/A - Not Applicable', value: 'na', disabled: false },
        ],
        fieldConfig: {
          placeholder: 'Select purpose',
        },
      },
      {
        key: 'purposeCustom',
        header: 'Purpose (other)',
        sortable: true,
        type: FIELD_TYPES.Text,
        group: 'Transaction Details',
        groupOrder: 1,
        fieldConfig: {
          placeholder: 'Enter purpose description',
        },
      },
      // 13. Deduct bank charges
      {
        key: 'deductBankCharges',
        header: 'Deduct Bank Charges',
        sortable: true,
        type: FIELD_TYPES.Select,
        required: true,
        group: 'Transaction Details',
        groupOrder: 2,
        options: [
          { label: 'Yes', value: 'yes', disabled: false },
          { label: 'No', value: 'no', disabled: false },
        ],
        fieldConfig: {
          placeholder: 'Select option',
        },
      },
      // 14. Payment Reference (Beneficiary)
      {
        key: 'reference',
        header: 'Payment Reference (Beneficiary)',
        sortable: true,
        type: FIELD_TYPES.Text,
        required: true,
        group: 'Transaction Details',
        groupOrder: 3,
        fieldConfig: {
          placeholder: 'Enter payment reference',
        },
      },
      // 15. Ledger Description (Internal Ref)
      {
        key: 'ledgerDesc',
        header: 'Ledger Description (Internal Ref)',
        sortable: true,
        type: FIELD_TYPES.Text,
        required: true,
        group: 'Transaction Details',
        groupOrder: 4,
        fieldConfig: {
          placeholder: 'Enter ledger description',
        },
      },
    ];

    // Always return full column set; conditional visibility is handled in the renderer layer
    const filteredColumns = allColumns.sort((a, b) => {
      // Sort by group first, then by groupOrder
      if (a.group !== b.group) {
        const groupOrder = [
          'Payment Details',
          'Banking Details',
          'Transaction Details',
        ];
        const aIndex = groupOrder.indexOf(a.group || 'default');
        const bIndex = groupOrder.indexOf(b.group || 'default');
        return aIndex - bIndex;
      }
      return (a.groupOrder || 0) - (b.groupOrder || 0);
    });

    return filteredColumns;
  }, []);

  // Helper function to create form fields from column keys
  const createFormFieldsFromKeys = useCallback(
    (keys: (keyof PaymentRow)[]): TableFormField[] => {
      return keys
        .map((key) => columns.find((c) => c.key === key))
        .filter(Boolean)
        .map((col) => {
          const c = col as Column<PaymentRow>;
          const isTextArea = !!c.extras?.textArea;
          const type = isTextArea
            ? 'textarea'
            : c.type === FIELD_TYPES.Select
              ? 'select'
              : c.type === FIELD_TYPES.Number
                ? 'number'
                : 'text';
          return createValidatedTableField(c, type, isTextArea);
        });
    },
    [columns, createValidatedTableField],
  );

  // Helper function to create currencyCustom field with dependencies
  const createCurrencyCustomField = useCallback((): TableFormField => {
    return {
      name: 'currencyCustom',
      label: 'Currency (other)',
      type: 'text',
      placeholder: 'Enter currency code/name',
      required: false,
      hidden: false,
      validate: (value: unknown) => {
        if (value && String(value).trim().length < 2) {
          return 'Currency code/name must be at least 2 characters';
        }
        return undefined;
      },
      dependencies: [
        {
          type: 'show',
          logicalOperator: 'AND',
          conditions: [
            {
              field: 'currency',
              operator: 'equals',
              value: 'other',
            },
          ],
        },
      ],
    };
  }, []);

  // Helper function to create purposeCustom field with dependencies
  const createPurposeCustomField = useCallback((): TableFormField => {
    return {
      name: 'purposeCustom',
      label: 'Purpose (other)',
      type: 'text',
      placeholder: 'Enter purpose description',
      required: false,
      hidden: false,
      validate: (value: unknown) => {
        if (value && String(value).trim().length < 2) {
          return 'Purpose description must be at least 2 characters';
        }
        return undefined;
      },
      dependencies: [
        {
          type: 'show',
          logicalOperator: 'AND',
          conditions: [
            {
              field: 'purpose',
              operator: 'equals',
              value: 'other',
            },
          ],
        },
      ],
    };
  }, []);

  // ============= Enhanced Table Configuration =============
  const tableField = useMemo(
    () => ({
      type: FIELD_TYPES.Table,
      name: 'gridData',
      label: 'Payment Details',
      required: true,
      disabled: disabledState.isTableDisabled(),
      extras: {
        table: {
          data: formState.data?.gridData || [],
          columns: columns,
          enableSort: true,
          enableAddRow: true,
          enableEdit: true,
          enableDelete: false,
          enableSearch: false,
          enableFilters: false,
          showRowNumbers: false,
          disabled: disabledState.isTableDisabled(),
          // Provide a modal form schema with dependencies (uses app's dependency processor)
          tableFormSchema: {
            useFormSchema: true,
            formSchema: {
              sections: [
                {
                  title: { text: 'Payment Details', type: 'h2' },
                  isGroupedAccordion: false,
                  fields: [],
                  sections: [
                    {
                      title: { text: '', type: 'h3' },
                      fields: [
                        // Basic payment fields
                        ...createFormFieldsFromKeys([
                          'location',
                          'payee',
                          'currency',
                        ]),
                        // Currency custom field with dependencies
                        createCurrencyCustomField(),
                        // Amount field
                        ...createFormFieldsFromKeys(['amount']),
                      ],
                      extras: {
                        grouped: true,
                        evenSpacing: true,
                        defaultExpanded: !disabledState.isFormCancelled,
                      },
                    },
                    {
                      title: { text: '', type: 'h3' },
                      fields: createFormFieldsFromKeys([
                        'accno',
                        'sortCode',
                        'iban',
                        'swiftBicCode',
                        'bankName',
                        'bankAddress',
                        'type',
                      ]),
                      extras: {
                        grouped: true,
                        evenSpacing: true,
                        defaultExpanded: !disabledState.isFormCancelled,
                      },
                    },
                    {
                      title: { text: '', type: 'h3' },
                      fields: [
                        ...createFormFieldsFromKeys(['purpose']),
                        // Purpose custom field with dependencies
                        createPurposeCustomField(),
                        ...createFormFieldsFromKeys([
                          'deductBankCharges',
                          'reference',
                          'ledgerDesc',
                        ]),
                      ],
                      extras: {
                        grouped: true,
                        evenSpacing: true,
                        defaultExpanded: !disabledState.isFormCancelled,
                      },
                    },
                  ],
                },
              ] as unknown as TableFormSection[],
            },
          },

          // ============= Enhanced Event Handlers =============
          onAddRow: (
            formData: Record<string, unknown>,
            context?: Record<string, unknown>,
          ) => {
            // Use centralized validation
            const validationContext = createValidationContext(
              formState,
              context,
            );
            const validationResult = validatePaymentRowForAdd(
              formData,
              validationContext,
            );

            console.log(
              '🔍 Schema onAddRow validation result:',
              validationResult,
            );

            if (!validationResult.success) {
              console.warn(
                '❌ Schema validation failed:',
                validationResult.errors,
              );
              return validationResult;
            }

            console.log(
              '✅ Schema validation passed, adding row:',
              validationResult.data,
            );
            return validationResult;
          },

          onUpdateRow: (
            formData: Record<string, unknown>,
            rowIndex: number,
            context?: Record<string, unknown>,
          ) => {
            // Use centralized validation
            const validationContext = createValidationContext(
              formState,
              context,
            );
            const validationResult = validatePaymentRowForUpdate(
              formData,
              rowIndex,
              validationContext,
            );

            console.log(
              '🔍 Schema onUpdateRow validation result:',
              validationResult,
            );

            if (!validationResult.success) {
              console.warn(
                '❌ Schema validation failed:',
                validationResult.errors,
              );
              return validationResult;
            }

            console.log(
              '✅ Schema validation passed, updating row:',
              validationResult.data,
            );
            return validationResult;
          },

          onDeleteRow: () => {
            // Update form state - this should trigger calculation
            // Note: updateFormData is returned from useFormState hook, not part of FormState
            // This will be handled by the FormField component
          },

          // ============= Custom Renderers =============
          customCellRenderer: (
            value: unknown,
            columnKey: string | number | symbol,
            rowData?: PaymentRow,
          ) => {
            if (columnKey === 'amount' && value) {
              // Get currency information from row data
              const currency = rowData?.currency || 'GBP';
              const currencyCustom = rowData?.currencyCustom;

              // Determine the currency symbol
              let currencySymbol = '';

              switch (currency) {
                case 'GBP':
                  currencySymbol = '£';
                  break;
                case 'EUR':
                  currencySymbol = '€';
                  break;
                case 'USD':
                  currencySymbol = '$';
                  break;
                case 'AED':
                  currencySymbol = 'د.إ';
                  break;
                case 'other':
                  // For custom currencies, try to determine symbol from common codes
                  if (currencyCustom) {
                    const customCurrency = String(currencyCustom)
                      .toUpperCase()
                      .trim();
                    const currencyMap: Record<string, string> = {
                      CAD: 'C$',
                      AUD: 'A$',
                      CHF: 'CHF',
                      JPY: '¥',
                      CNY: '¥',
                      INR: '₹',
                      SEK: 'kr',
                      NOK: 'kr',
                      DKK: 'kr',
                      ZAR: 'R',
                      BRL: 'R$',
                      MXN: '$',
                      SGD: 'S$',
                      HKD: 'HK$',
                      NZD: 'NZ$',
                      TRY: '₺',
                      RUB: '₽',
                      KRW: '₩',
                      THB: '฿',
                      MYR: 'RM',
                      PHP: '₱',
                      IDR: 'Rp',
                      VND: '₫',
                    };
                    currencySymbol = currencyMap[customCurrency] || '';
                  }
                  break;
                default:
                  // Default to £ for unknown currencies
                  currencySymbol = '£';
              }

              const formattedAmount = formatCurrencyValue(
                value as string,
                currency,
                false,
              );
              return currencySymbol
                ? `${currencySymbol}${formattedAmount}`
                : formattedAmount;
            }
          },

          // ============= Search and Filter Configuration =============
          searchPlaceholder: 'Search payment details...',
          onSearch: (term: string, data: PaymentRow[]) => {
            console.log('Searching for:', term, 'in', data.length, 'rows');
          },

          // ============= Row Limits =============
          maxRows: 5,
          onMaxRowsReached: (currentCount: number) => {
            console.warn(`Maximum ${currentCount} rows reached`);
          },
        },
      },
      dependencies: [
        {
          type: 'show' as const,
          logicalOperator: 'AND' as const,
          conditions: [
            {
              field: 'payments_count',
              operator: 'equals' as const,
              value: '1-5Payments',
            },
          ],
        },
      ],
      validate: (value: unknown) => {
        // Only validate when 1-5 payments is selected
        if (!disabledState.isSinglePaymentMode) {
          return undefined;
        }

        // Handle different data structures
        let tableValue: PaymentRow[] = [];

        if (Array.isArray(value)) {
          // Direct array structure
          tableValue = value as PaymentRow[];
        } else if (value && typeof value === 'object' && 'data' in value) {
          // Object with data property structure
          const dataValue = (value as Record<string, unknown>).data;
          if (Array.isArray(dataValue)) {
            tableValue = dataValue as PaymentRow[];
          }
        }

        if (tableValue.length === 0) {
          return 'Please add at least one payment detail';
        }
        // Validate each row
        for (const [i, row] of tableValue.entries()) {
          // Determine location at row level, fallback to global if available
          const globalLocation =
            (formState.data.location as 'uk' | 'nonUk' | undefined) || 'nonUk';
          const effectiveRowLocation: 'uk' | 'nonUk' =
            globalLocation || 'nonUk';

          if (!row.payee) return `Row ${i + 1}: Payee is required`;
          if (!row.currency) return `Row ${i + 1}: Currency is required`;

          // Validate currencyCustom field when currency is "other"
          if (row.currency === 'other') {
            if (
              !row.currencyCustom ||
              String(row.currencyCustom).trim() === ''
            ) {
              return `Row ${
                i + 1
              }: Currency code/name is required when "Other" is selected`;
            }
            if (String(row.currencyCustom).trim().length < 2) {
              return `Row ${
                i + 1
              }: Currency code/name must be at least 2 characters`;
            }
          }

          if (
            !row.amount ||
            isNaN(Number(row.amount)) ||
            Number(row.amount) <= 0
          )
            return `Row ${i + 1}: Amount must be a positive number`;
          // Require banking fields based on location
          if (effectiveRowLocation === 'uk') {
            if (!row.accno) return `Row ${i + 1}: Account number is required`;
            // Optionally require sort code if present in your process
            // if (!row.sortCode) return `Row ${i + 1}: Sort code is required`;
          } else if (effectiveRowLocation === 'nonUk') {
            if (!row.iban) return `Row ${i + 1}: IBAN is required`;
            if (!row.swiftBicCode)
              return `Row ${i + 1}: SWIFT/BIC code is required`;
            if (!row.bankName) return `Row ${i + 1}: Bank name is required`;
          }
          if (!row.type) return `Row ${i + 1}: Account type is required`;
          if (!row.purpose) return `Row ${i + 1}: Purpose is required`;

          // Validate purposeCustom field when purpose is "other"
          if (row.purpose === 'other') {
            if (!row.purposeCustom || String(row.purposeCustom).trim() === '') {
              return `Row ${
                i + 1
              }: Purpose description is required when "Other" is selected`;
            }
            if (String(row.purposeCustom).trim().length < 2) {
              return `Row ${
                i + 1
              }: Purpose description must be at least 2 characters`;
            }
          }

          if (!row.reference)
            return `Row ${i + 1}: Payment reference is required`;
          if (!row.ledgerDesc)
            return `Row ${i + 1}: Ledger description is required`;
        }
        return undefined;
      },
    }),
    [
      disabledState,
      columns,
      createFormFieldsFromKeys,
      createCurrencyCustomField,
      createPurposeCustomField,
      formState,
    ],
  );

  /**
   * The main form schema is memoized to avoid unnecessary re-computations.
   * The "modern" dependency format is used here, describing conditions via
   * a conditions array alongside a logicalOperator.
   */
  const formSchema = useMemo<FormSchemaConfig>(
    () => ({
      sections: [
        {
          isGrouped: true,
          isGroupedAccordion: true,
          sections: [
            {
              title: { text: 'Client Details' },
              layout: 'side-by-side',
              fields: [
                {
                  type: 'text',
                  name: 'client_name',
                  label: 'Client',
                  disabled: disabledState.isFieldDisabled('readonly'),
                },
                {
                  type: 'text',
                  name: 'matter_name',
                  label: 'Matter',
                  disabled: disabledState.isFieldDisabled('readonly'),
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: false,
              },
            },
            {
              title: { text: 'Payment Details', type: 'h3' },
              layout: 'single',
              sections: [],
              fields: [
                {
                  type: 'radio',
                  name: 'interest',
                  label:
                    'A requirement of SAR is to account to the client a fair sum of Interest. Is Interest to be added to this payment?',
                  options: [
                    { value: 'yes', label: 'Yes', disabled: false },
                    { value: 'no', label: 'No', disabled: false },
                  ],
                  required: true,
                  validate: (value: unknown) =>
                    !value
                      ? 'Please select if interest is to be added'
                      : undefined,
                  disabled: disabledState.isFieldDisabled('conditional'),
                },
                {
                  type: 'customComponent',
                  customComponent: (
                    <WarningText
                      text="You may by a written agreement come to a different arrangement with the client or the third party for whom the money is held as to the payment of interest, but you must provide sufficient information to enable them to give informed consent."
                      size="small"
                      showIcon
                    />
                  ),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'AND',
                      conditions: [
                        {
                          field: 'interest',
                          operator: 'equals',
                          value: 'no',
                        },
                      ],
                    },
                  ],
                  name: '',
                  label: 'customComponentInterest',
                },
                {
                  type: 'select',
                  name: 'interest_options',
                  label: 'Please select from the interest options:',
                  disabled: disabledState.isFieldDisabled('conditional'),
                  options: [
                    {
                      value: 'addToPayment',
                      label:
                        'Client Cashiers to add interest due on Principal Amount',
                      disabled: disabledState.isFieldDisabled('conditional'),
                    },
                    {
                      value: 'bespokeCalculations',
                      label: 'Bespoke interest to be calculated',
                      disabled: disabledState.isFieldDisabled('conditional'),
                    },
                  ],
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'AND',
                      conditions: [
                        {
                          field: 'interest',
                          operator: 'equals',
                          value: 'yes',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'customComponent',
                  customComponent: (
                    <WarningText
                      text="Please contact Client Cashiers to discuss"
                      size="small"
                      showIcon
                    />
                  ),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'AND',
                      conditions: [
                        {
                          field: 'interest_options',
                          operator: 'equals',
                          value: 'bespokeCalculations',
                        },
                        {
                          field: 'interest',
                          operator: 'equals',
                          value: 'yes',
                        },
                      ],
                    },
                  ],
                  name: 'customComponentCashiers',
                  label: '',
                },
                {
                  type: 'radio',
                  name: 'payments_count',
                  label: 'How many payments are included on this request?',
                  options: [
                    {
                      value: '1-5Payments',
                      label: '1-5 payments',
                      disabled: false,
                    },
                    {
                      value: '5+Payments',
                      label: '5+ Payments',
                      disabled: false,
                    },
                  ],
                  required: true,
                  validate: (value) =>
                    !value ? 'Please select the number of payments' : undefined,
                  disabled: disabledState.isFieldDisabled('conditional'),
                },
                tableField,
                // Show existing files when form is completed
                ...(disabledState.isFormCompleted &&
                formState.data.payment_spreadsheet
                  ? [
                      {
                        type: 'customComponent' as const,
                        customComponent: (
                          <FileDisplay
                            fileName={formState.data?.payment_spreadsheet}
                            workflowId={formState.data?.id}
                            workflowType="dubai-payment-approval"
                          />
                        ),
                        dependencies: [
                          {
                            type: 'show' as const,
                            logicalOperator: 'AND' as const,
                            conditions: [
                              {
                                field: 'payments_count',
                                operator: 'equals' as const,
                                value: '5+Payments',
                              },
                            ],
                          },
                        ],
                        name: 'payment_spreadsheet_display',
                        label: 'Payments Request Spreadsheet:',
                      },
                    ]
                  : []),
                // Show existing files when rejected (but still allow uploads)
                ...(formState.isRejected && formState.data.payment_spreadsheet
                  ? [
                      {
                        type: 'customComponent' as const,
                        customComponent: (
                          <FileDisplay
                            fileName={formState.data?.payment_spreadsheet}
                            workflowId={formState.data?.id}
                            workflowType="dubai-payment-approval"
                          />
                        ),
                        dependencies: [
                          {
                            type: 'show' as const,
                            logicalOperator: 'AND' as const,
                            conditions: [
                              {
                                field: 'payments_count',
                                operator: 'equals' as const,
                                value: '5+Payments',
                              },
                            ],
                          },
                        ],
                        name: 'payment_spreadsheet_display_rejected',
                        label:
                          'Previously Uploaded Payments Request Spreadsheet:',
                      },
                    ]
                  : []),
                // Always show file upload field (unless form is completed and not rejected)
                ...(!disabledState.isFormCompleted || formState.isRejected
                  ? [
                      {
                        type: 'file' as const,
                        name: 'payment_spreadsheet',
                        label: formState.isRejected
                          ? 'Upload Additional Payments Request Spreadsheet (if needed):'
                          : 'Please Upload Payments Request Spreadsheet:',
                        tooltip: 'Document Viewer only supports: .pdf',
                        disabled: disabledState.isFileUploadDisabled(),
                        required: !formState.isRejected, // Not required when rejected since we already have files
                        extras: {
                          file: {
                            multiple: true,
                          },
                        },
                        validate: (value: unknown) => {
                          // Only required if payments_count is '5+Payments' and not rejected
                          if (
                            disabledState.isMultiplePaymentMode &&
                            !value &&
                            !formState.isRejected
                          ) {
                            return 'Please upload the payments spreadsheet';
                          }
                          return undefined;
                        },
                        dependencies: [
                          {
                            type: 'show' as const,
                            logicalOperator: 'AND' as const,
                            conditions: [
                              {
                                field: 'payments_count',
                                operator: 'equals' as const,
                                value: '5+Payments',
                              },
                            ],
                          },
                        ],
                      },
                    ]
                  : []),
                {
                  type: 'number',
                  name: 'payment_total',
                  label: 'Payment Total',
                  placeholder: disabledState.isMultiplePaymentMode
                    ? 'Payment total...'
                    : undefined,
                  disabled: disabledState.isPaymentTotalDisabled(),
                  required: disabledState.isMultiplePaymentMode,
                  extras: {
                    textInput: {
                      formatCurrency: true,
                    },
                  },
                  validate: disabledState.isMultiplePaymentMode
                    ? (value) =>
                        !value ? 'Please provide a payment Total' : undefined
                    : undefined,
                  dependencies: [
                    {
                      type: 'hide',
                      logicalOperator: 'AND',
                      conditions: [
                        {
                          field: 'payments_count',
                          operator: 'equals',
                          value: '',
                        },
                      ],
                    },
                  ],
                },
                // Show existing files when form is completed
                ...(disabledState.isFormCompleted &&
                formState.data.payment_attachment
                  ? [
                      {
                        type: 'customComponent' as const,
                        customComponent: (
                          <FileDisplay
                            fileName={formState.data?.payment_attachment}
                            workflowId={formState.data?.id}
                            workflowType="dubai-payment-approval"
                          />
                        ),
                        name: 'payment_attachment_display',
                        label: 'Relevant Documentation:',
                      },
                    ]
                  : []),
                // Show existing files when rejected (but still allow uploads)
                ...(formState.isRejected && formState.data.payment_attachment
                  ? [
                      {
                        type: 'customComponent' as const,
                        customComponent: (
                          <FileDisplay
                            fileName={formState.data?.payment_attachment}
                            workflowId={formState.data?.id}
                            workflowType="dubai-payment-approval"
                          />
                        ),
                        name: 'payment_attachment_display_rejected',
                        label: 'Previously Uploaded Documentation:',
                      },
                    ]
                  : []),
                // Always show file upload field (unless form is completed and not rejected)
                ...(!disabledState.isFormCompleted || formState.isRejected
                  ? [
                      {
                        type: 'file' as const,
                        name: 'payment_attachment',
                        label: formState.isRejected
                          ? 'Upload Additional Documentation (if needed):'
                          : 'Please upload any relevant documentation:',
                        tooltip: 'Document Viewer only supports: .pdf',
                        disabled: disabledState.isFieldDisabled('conditional'),
                        extras: {
                          file: {
                            multiple: true,
                          },
                        },
                      },
                    ]
                  : []),
                {
                  type: 'textArea',
                  name: 'comments',
                  label: 'Any other comments:',
                  extras: {
                    textArea: {
                      maxLength: 500,
                      showCharCount: true,
                      rows: 4,
                      autoResize: true,
                    },
                  },
                  disabled: disabledState.isTextAreaDisabled(),
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: !disabledState.isFormCancelled,
              },
            },
            {
              title: { text: 'Approvers', type: 'h3' },
              layout: 'side-by-side',
              fields: [
                {
                  type: 'tagPicker',
                  name: 'responsible_lawyer',
                  label: 'Responsible Lawyer',
                  required: true,
                  validate: (value) =>
                    !value || (Array.isArray(value) && value.length === 0)
                      ? 'Responsible Lawyer is required'
                      : undefined,
                  disabled: disabledState.isTagPickerDisabled(),
                  extras: {
                    tagPicker: {
                      minSearchLength: 4,
                      maxTags: 1,
                      createNewTags: false,
                    },
                  },
                },
                {
                  type: 'tagPicker',
                  name: 'first_partner',
                  label: 'First Partner',
                  required: true,
                  validate: (value) =>
                    !value || (Array.isArray(value) && value.length === 0)
                      ? 'First Partner is required'
                      : undefined,
                  disabled: disabledState.isTagPickerDisabled(),
                  extras: {
                    tagPicker: {
                      minSearchLength: 4,
                      maxTags: 1,
                      createNewTags: false,
                    },
                  },
                },
                {
                  type: 'tagPicker',
                  name: 'second_partner_above_25k',
                  label: 'Second Partner',
                  required: true,
                  validate: (value, formData) => {
                    const isRequired = formData?.second_partner_required === 1;
                    if (isRequired) {
                      return !value ||
                        (Array.isArray(value) && value.length === 0)
                        ? 'Second Partner is required for payments above £25,000'
                        : undefined;
                    }
                    return undefined;
                  },
                  disabled: disabledState.isTagPickerDisabled(),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'payment_total',
                          operator: 'greaterThan',
                          value: 24999,
                        },
                        {
                          field: 'payment_total',
                          operator: 'equals',
                          value: 25000,
                        },
                      ],
                    },
                  ],
                  extras: {
                    tooltip:
                      'Second Partner is required for payments above £25,000',
                    tagPicker: {
                      minSearchLength: 4,
                      maxTags: 1,
                      createNewTags: false,
                    },
                  },
                },
                {
                  type: 'tagPicker',
                  name: 'second_partner_below_25k',
                  label: 'Second Partner (Optional)',
                  required: false,
                  disabled: disabledState.isTagPickerDisabled(),
                  dependencies: [
                    {
                      type: 'show',
                      logicalOperator: 'OR',
                      conditions: [
                        {
                          field: 'payment_total',
                          operator: 'lessThan',
                          value: 25000,
                        },
                      ],
                    },
                  ],
                  extras: {
                    tooltip:
                      'Second Partner is required for payments above £25,000',
                    tagPicker: {
                      minSearchLength: 4,
                      maxTags: 1,
                      createNewTags: false,
                    },
                  },
                },
              ],
              extras: {
                grouped: true,
                evenSpacing: true,
                defaultExpanded: !disabledState.isFormCancelled,
              },
            },
          ],
        },
      ],
      calculations: {
        ...(disabledState.isSinglePaymentMode
          ? {
              payment_total: {
                type: 'custom',
                sourceFields: ['gridData'],
                customCalculation: calculatePaymentTotal,
                roundTo: 2,
                formatOptions: {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              },
            }
          : {}),
        // Calculate second partner field properties based on payment total
        second_partner_required: {
          type: 'custom',
          sourceFields: ['gridData', 'payment_total'],
          customCalculation: (
            _values: unknown[],
            formData: Record<string, unknown>,
          ) => {
            // First check if payment_total is directly set (for 5+ payments mode)
            const directPaymentTotal =
              parseFloat(String(formData.payment_total)) || 0;
            if (directPaymentTotal > 0) {
              console.log('Using direct payment_total:', directPaymentTotal);
              return directPaymentTotal >= 25000 ? 1 : 0;
            }

            // Otherwise calculate from gridData (for 1-5 payments mode)
            const gridData = formData.gridData || [];
            const rowsToProcess = Array.isArray(
              (gridData as Record<string, unknown>).data,
            )
              ? (gridData as Record<string, unknown>).data
              : Array.isArray(gridData)
                ? gridData
                : [];

            const calculatedPaymentTotal = (
              rowsToProcess as PaymentRow[]
            ).reduce((total: number, row: PaymentRow) => {
              const amount = parseFloat(row.amount) || 0;
              return total + amount;
            }, 0);

            console.log(
              'Using calculated payment_total from gridData:',
              calculatedPaymentTotal,
            );
            return calculatedPaymentTotal > 25000 ? 1 : 0; // Return 1 for true, 0 for false
          },
        },
        second_partner_label: {
          type: 'custom',
          sourceFields: ['gridData', 'payment_total'],
          customCalculation: (
            _values: unknown[],
            formData: Record<string, unknown>,
          ) => {
            // First check if payment_total is directly set (for 5+ payments mode)
            const directPaymentTotal =
              parseFloat(String(formData.payment_total)) || 0;
            if (directPaymentTotal > 0) {
              console.log(
                'Using direct payment_total for label:',
                directPaymentTotal,
              );
              return directPaymentTotal > 25000 ? 1 : 0;
            }

            // Otherwise calculate from gridData (for 1-5 payments mode)
            const gridData = formData.gridData || [];
            const rowsToProcess = Array.isArray(
              (gridData as Record<string, unknown>).data,
            )
              ? (gridData as Record<string, unknown>).data
              : Array.isArray(gridData)
                ? gridData
                : [];

            const calculatedPaymentTotal = (
              rowsToProcess as PaymentRow[]
            ).reduce((total: number, row: PaymentRow) => {
              const amount = parseFloat(row.amount) || 0;
              return total + amount;
            }, 0);

            console.log(
              'Using calculated payment_total for label from gridData:',
              calculatedPaymentTotal,
            );
            return calculatedPaymentTotal > 25000 ? 1 : 0; // Return 1 for "required" label, 0 for "optional" label
          },
        },
      },
    }),
    [
      disabledState,
      tableField,
      formState.data?.payment_attachment,
      formState.data?.payment_spreadsheet,
      formState.data?.id,
      formState.isRejected,
    ],
  );

  // The returned object is the complete form schema, ready for rendering
  return formSchema;
};
